import { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button.tsx';
import { Label } from '../ui/label.tsx';
import type { ReleaseTask } from './ReleaseTasksPage.tsx';
import Step2Panel from './Step2Panel.tsx';
import Step3Panel from './Step3Panel.tsx';
import type { ComparisonResult } from './Step3Panel.tsx';
import { getExperiments, getReleaseTaskById } from '../../../services/coaReleaseService.ts';

interface BatchOption {
  batchNumber: string;
  latestCreatedAt: string;
}

interface CreateReleaseTaskProps {
  onBack: () => void;
  onSave: (task: ReleaseTask, results: ComparisonResult[]) => void;
}

const formatNzDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const m = parts.reduce<Record<string, string>>((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${m.year}-${m.month}-${m.day} ${m.hour}:${m.minute}:${m.second}`;
};

export default function CreateReleaseTask({ onBack, onSave }: CreateReleaseTaskProps) {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const taskId = searchParams.get('taskId');
  const sourceTaskId = searchParams.get('sourceTaskId');
  const isViewMode = mode === 'view';
  const isRetestMode = mode === 'retest';

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [batchOptions, setBatchOptions] = useState<BatchOption[]>([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [step3InitialResults, setStep3InitialResults] = useState<ComparisonResult[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    if (isViewMode) return;

    const fetchBatchOptions = async () => {
      setLoadingData(true);
      setErrorText('');
      try {
        const experiments = await getExperiments({ range: '[0,9999]', perPage: '10000' });
        const map = new Map<string, string>();
        experiments.forEach((item) => {
          const batch = String(item.batchNumber || '').trim();
          if (!batch) return;
          const current = map.get(batch);
          if (!current || new Date(item.createdAt).getTime() > new Date(current).getTime()) {
            map.set(batch, item.createdAt);
          }
        });
        const options = Array.from(map.entries())
          .map(([batchNumber, latestCreatedAt]) => ({ batchNumber, latestCreatedAt }))
          .sort((a, b) => new Date(b.latestCreatedAt).getTime() - new Date(a.latestCreatedAt).getTime());
        setBatchOptions(options);
      } catch (error) {
        setErrorText(error instanceof Error ? error.message : 'Load batches failed.');
      } finally {
        setLoadingData(false);
      }
    };

    void fetchBatchOptions();
  }, [isViewMode]);

  useEffect(() => {
    const batchFromQuery = searchParams.get('batchNumber') || '';
    if (batchFromQuery) setSelectedBatch(batchFromQuery);
  }, [searchParams]);

  useEffect(() => {
    if (!isViewMode) return;

    const resolveViewBatch = async () => {
      setLoadingData(true);
      setErrorText('');
      try {
        let batchNumber = searchParams.get('batchNumber') || '';
        if (!batchNumber && taskId) {
          const task = await getReleaseTaskById(taskId);
          batchNumber = String(task?.batchNumber || '');
        }
        if (!batchNumber) throw new Error('Batch number not found in view mode.');
        setSelectedBatch(batchNumber);
        setStep(3);
      } catch (error) {
        setErrorText(error instanceof Error ? error.message : 'Load view mode data failed.');
      } finally {
        setLoadingData(false);
      }
    };

    void resolveViewBatch();
  }, [isViewMode, searchParams, taskId]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={onBack} className="hover:bg-gray-50">
          <ArrowLeft className="size-4 mr-2" />
          Back
        </Button>
        <div>
          <h2 className="text-2xl">Create Release Task</h2>
          <p className="text-sm text-gray-500">3 Steps: Release batch -&gt; Confirmate Specifications -&gt; Release result</p>
        </div>
      </div>

      {!isViewMode && (
        <div className="flex items-center justify-center gap-4 py-4">
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${step === 1 ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'}`}>1</div>
            <span className={step === 1 ? 'text-blue-600 text-sm' : 'text-gray-600 text-sm'}>Release batch</span>
          </div>
          <div className={`h-1 w-14 rounded ${step > 1 ? 'bg-blue-500' : 'bg-gray-300'}`} />
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${step === 2 ? 'bg-blue-500 text-white' : step > 2 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-700'}`}>2</div>
            <span className={step === 2 ? 'text-blue-600 text-sm' : 'text-gray-600 text-sm'}>Confirmate Specifications</span>
          </div>
          <div className={`h-1 w-14 rounded ${step > 2 ? 'bg-blue-500' : 'bg-gray-300'}`} />
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${step === 3 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'}`}>3</div>
            <span className={step === 3 ? 'text-blue-600 text-sm' : 'text-gray-600 text-sm'}>Release result</span>
          </div>
        </div>
      )}

      {errorText && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {errorText}
        </div>
      )}

      {!isViewMode && step === 1 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h3 className="text-lg">Step 1: Release batch</h3>
              <span className="text-xs text-gray-500">Distinct from Experiment.BatchNumber (latest first)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                disabled={!selectedBatch || loadingData}
                onClick={() => {
                  const value = selectedBatch.trim();
                  if (!isRetestMode) {
                    const exact = batchOptions.some((item) => item.batchNumber === value);
                    if (!exact) {
                      setErrorText('Please select an existing batch number from the dropdown list.');
                      return;
                    }
                  }
                  setErrorText('');
                  setStep(2);
                }}
              >
                Next
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="batchSelectInput">Release batch</Label>
            <input
              id="batchSelectInput"
              list="release-batch-options"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              placeholder="Type batch number to search, then select from dropdown"
              disabled={loadingData}
              readOnly={isRetestMode && !!searchParams.get('batchNumber')}
            />
            <datalist id="release-batch-options">
              {batchOptions.map((item) => (
                <option key={item.batchNumber} value={item.batchNumber}>
                  {item.batchNumber} ({formatNzDateTime(item.latestCreatedAt)})
                </option>
              ))}
            </datalist>
            <p className="text-xs text-gray-500">Input to filter, then select one batch from the dropdown list.</p>
          </div>
        </div>
      )}

      {!isViewMode && step === 2 && (
        <Step2Panel
          selectedBatch={selectedBatch}
          isRetestMode={isRetestMode}
          onBack={() => setStep(1)}
          onApplySuccess={(results) => {
            setStep3InitialResults(results);
            setStep(3);
          }}
          onLoadingChange={setLoadingData}
          onErrorChange={setErrorText}
        />
      )}

      {step === 3 && (
        <Step3Panel
          selectedBatch={selectedBatch}
          isViewMode={isViewMode}
          isRetestMode={isRetestMode}
          taskId={taskId}
          sourceTaskId={sourceTaskId}
          initialResults={step3InitialResults}
          onBack={() => setStep(2)}
          onSave={onSave}
          onLoadingChange={setLoadingData}
          onErrorChange={setErrorText}
        />
      )}

      {loadingData && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="rounded-lg border border-gray-200 bg-white px-6 py-4 text-sm text-gray-700 shadow-lg">
            Loading...
          </div>
        </div>
      )}
    </div>
  );
}
